import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  UserCog, Search, Save, Check, X, Users,
  ArrowLeft, RefreshCw, Filter
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AssignRoles() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [staffList, setStaffList] = useState([]);
  const [filteredStaff, setFilteredStaff] = useState([]);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [selectedRoles, setSelectedRoles] = useState([]);

  useEffect(() => {
    fetchStaff();
    fetchRoles();
  }, []);

  useEffect(() => {
    if (searchQuery.trim()) {
      const filtered = staffList.filter(s => 
        s.full_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        s.employee_id?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        s.designation?.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredStaff(filtered);
    } else {
      setFilteredStaff(staffList);
    }
  }, [searchQuery, staffList]);

  const fetchStaff = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/staff/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setStaffList(data || []);
        setFilteredStaff(data || []);
      }
    } catch (error) {
      console.error('Error fetching staff:', error);
      toast.error('Failed to load staff list');
    } finally {
      setLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/roles/list`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAvailableRoles(data.roles || []);
      }
    } catch (error) {
      console.error('Error fetching roles:', error);
    }
  };

  const selectStaff = async (staff) => {
    setSelectedStaff(staff);
    
    // Fetch current roles
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/roles/staff/${staff.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSelectedRoles(data.roles || []);
      }
    } catch (error) {
      console.error('Error fetching staff roles:', error);
      setSelectedRoles(staff.roles || []);
    }
  };

  const toggleRole = (roleKey) => {
    setSelectedRoles(prev => {
      if (prev.includes(roleKey)) {
        return prev.filter(r => r !== roleKey);
      } else {
        return [...prev, roleKey];
      }
    });
  };

  const handleSave = async () => {
    if (!selectedStaff) return;
    
    if (selectedRoles.length === 0) {
      toast.error('Please select at least one role');
      return;
    }
    
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/roles/assign/${selectedStaff.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ roles: selectedRoles })
      });
      
      if (response.ok) {
        toast.success(`Roles assigned to ${selectedStaff.full_name}`);
        // Update local state
        setStaffList(prev => prev.map(s => 
          s.id === selectedStaff.id ? { ...s, roles: selectedRoles } : s
        ));
        setSelectedStaff(prev => ({ ...prev, roles: selectedRoles }));
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to assign roles');
      }
    } catch (error) {
      console.error('Error assigning roles:', error);
      toast.error('Failed to assign roles');
    } finally {
      setSaving(false);
    }
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      'Admin': 'bg-red-100 text-red-700',
      'Accountant': 'bg-green-100 text-green-700',
      'HR Manager': 'bg-blue-100 text-blue-700',
      'Teacher': 'bg-purple-100 text-purple-700',
      'Librarian': 'bg-yellow-100 text-yellow-700',
      'Front Desk': 'bg-pink-100 text-pink-700',
      'Exam Controller': 'bg-orange-100 text-orange-700',
      'Admission Officer': 'bg-cyan-100 text-cyan-700'
    };
    return colors[role] || 'bg-gray-100 text-gray-700';
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="outline" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4 mr-2" /> Back
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-gray-800">Assign Roles</h1>
            <p className="text-gray-600 mt-1">Quickly assign or change staff roles</p>
          </div>
        </div>
        <Button variant="outline" onClick={fetchStaff}>
          <RefreshCw className="h-4 w-4 mr-2" /> Refresh
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Staff List */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Select Staff
            </CardTitle>
          </CardHeader>
          <CardContent>
            {/* Search */}
            <div className="mb-4">
              <div className="relative">
                <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search by name, ID, or designation..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg"
                />
              </div>
            </div>

            {/* Staff List */}
            <div className="max-h-[500px] overflow-y-auto space-y-2">
              {loading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-600"></div>
                </div>
              ) : filteredStaff.length === 0 ? (
                <p className="text-center py-8 text-gray-500">No staff found</p>
              ) : (
                filteredStaff.map((staff) => (
                  <div
                    key={staff.id}
                    onClick={() => selectStaff(staff)}
                    className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                      selectedStaff?.id === staff.id 
                        ? 'border-blue-500 bg-blue-50' 
                        : 'border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-medium">{staff.full_name}</p>
                        <p className="text-sm text-gray-500">{staff.employee_id} • {staff.designation}</p>
                      </div>
                      {selectedStaff?.id === staff.id && (
                        <Check className="h-5 w-5 text-blue-500" />
                      )}
                    </div>
                    {/* Current Roles */}
                    <div className="mt-2 flex flex-wrap gap-1">
                      {(staff.roles || []).length > 0 ? (
                        staff.roles.map((role) => (
                          <span key={role} className={`px-2 py-0.5 rounded-full text-xs ${getRoleBadgeColor(role)}`}>
                            {role}
                          </span>
                        ))
                      ) : (
                        <span className="text-xs text-gray-400">No roles assigned</span>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>

        {/* Role Assignment */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <UserCog className="h-5 w-5" />
              {selectedStaff ? `Assign Roles to ${selectedStaff.full_name}` : 'Select a Staff Member'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {!selectedStaff ? (
              <div className="text-center py-12 text-gray-500">
                <UserCog className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>Select a staff member from the list to assign roles</p>
              </div>
            ) : (
              <>
                {/* Staff Info */}
                <div className="mb-6 p-4 bg-blue-50 rounded-lg">
                  <p className="font-medium text-blue-800">{selectedStaff.full_name}</p>
                  <p className="text-sm text-blue-600">{selectedStaff.employee_id} • {selectedStaff.designation}</p>
                  <p className="text-sm text-blue-500">{selectedStaff.department}</p>
                </div>

                {/* Role Selection */}
                <p className="text-sm font-medium text-gray-700 mb-3">Select Roles (Multiple allowed):</p>
                <div className="space-y-2 max-h-[350px] overflow-y-auto">
                  {availableRoles.map((role) => (
                    <label
                      key={role.key}
                      className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                        selectedRoles.includes(role.key)
                          ? 'border-green-500 bg-green-50'
                          : 'border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedRoles.includes(role.key)}
                        onChange={() => toggleRole(role.key)}
                        className="mt-1 h-4 w-4 rounded"
                      />
                      <div className="flex-1">
                        <p className="font-medium">{role.name}</p>
                        <p className="text-sm text-gray-500">{role.description}</p>
                      </div>
                    </label>
                  ))}
                </div>

                {/* Selected Roles Summary */}
                {selectedRoles.length > 0 && (
                  <div className="mt-4 p-3 bg-gray-100 rounded-lg">
                    <p className="text-sm font-medium text-gray-700 mb-2">Selected Roles:</p>
                    <div className="flex flex-wrap gap-2">
                      {selectedRoles.map((role) => (
                        <span key={role} className={`px-2 py-1 rounded-full text-sm ${getRoleBadgeColor(role)}`}>
                          {role}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Save Button */}
                <div className="mt-6 flex justify-end">
                  <Button 
                    onClick={handleSave} 
                    disabled={saving || selectedRoles.length === 0}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    <Save className="h-4 w-4 mr-2" />
                    {saving ? 'Saving...' : 'Save Roles'}
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
